import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncStatus } from "@react-hookz/web";

import { notificationStore, NotificationType } from "../store/notification";
import type { IHubContentSubType } from "../types/ihubContentType";
import { IHubContentType } from "../types/ihubContentType";
import { IHubContentProvider, MarketingContentType } from "../types/providers";

export const isLoading = (status: AsyncStatus): boolean => status === "loading";

export const isSuccess = (status: AsyncStatus): boolean => status === "success";

export const isError = (status: AsyncStatus): boolean => status === "error";

export const isNotExecuted = (status: AsyncStatus): boolean =>
    status === "not-executed";

export const isNotExecutedOrLoading = (status: AsyncStatus): boolean =>
    isLoading(status) || isNotExecuted(status);

export const generateShareableUrl = (
    baseUrl: string,
    params: Record<string, string>,
): string => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value) {
            searchParams.append(key, value);
        }
    });
    const queryString = searchParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

const sharableLinkDelim = "||";
const sharableLinkError = (message: string) => {
    // TODO: We should prompt the user to submit a ticket via Aquila
    notificationStore
        .getState()
        .upsertNotification(
            "Failed to create or load sharable link",
            NotificationType.ERROR,
        );
    console.error(message);
    throw new Error(message);
};

export const generateSharableLinkId = async (
    identifier: string,
    insightType: InsightType | undefined,
): Promise<string> => {
    if (!identifier)
        sharableLinkError(
            "identifier was falsey when generating sharable link",
        );

    return [insightType, identifier].join(sharableLinkDelim);
};

export const generateSharableLinkIdSync = (
    identifier: string,
    insightType: InsightType | undefined,
): string => {
    if (!identifier)
        sharableLinkError(
            "identifier was falsey when generating sharable link",
        );

    return [insightType, identifier].join(sharableLinkDelim);
};

export const parseSharableLinkId = (
    sharableLinkId: string,
): {
    identifier: string;
    contentType: IHubContentType;
    contentSubType: IHubContentSubType;
} => {
    if (!sharableLinkId) {
        throw new Error("Sharable link ID is required");
    }

    const firstDelimIndex = sharableLinkId.indexOf(sharableLinkDelim);
    const marketingContentFallback = {
        contentType: IHubContentType.MARKETING_CONTENT,
        contentSubType: {
            provider: IHubContentProvider.MARKETING_NARRATIVES,
            scope: MarketingContentType.ALL,
        },
    };

    // Handle legacy format (just identifier)
    if (firstDelimIndex === -1) {
        return {
            identifier: sharableLinkId,
            ...marketingContentFallback,
        };
    }

    // Handle new format (insightType||identifier)
    const insightType = sharableLinkId.slice(0, firstDelimIndex);
    const identifier = sharableLinkId.slice(
        firstDelimIndex + sharableLinkDelim.length,
    );

    // Look up content type and provider from scope
    const typeInfo = InsightTypeToContentTypeMap[insightType];

    if (!typeInfo) {
        console.warn(
            `Unknown insightType: ${insightType}, falling back to default content type`,
        );
        return {
            identifier,
            ...marketingContentFallback,
        };
    }

    return {
        identifier,
        contentType: typeInfo.contentType,
        contentSubType: {
            provider: typeInfo.provider,
        },
    };
};

export const InsightTypeToContentTypeMap: Record<
    string,
    { contentType: IHubContentType; provider: IHubContentProvider }
> = {
    [InsightType.ISTORE]: {
        contentType: IHubContentType.INSIGHTS,
        provider: IHubContentProvider.INSIGHT_FAMILIES,
    },
    [InsightType.INSIGHT]: {
        contentType: IHubContentType.INSIGHTS,
        provider: IHubContentProvider.INSIGHT_FAMILIES,
    },
    [InsightType.PACKAGING]: {
        contentType: IHubContentType.INSIGHTS,
        provider: IHubContentProvider.PACKAGES,
    },
    [InsightType.MARKETING]: {
        contentType: IHubContentType.MARKETING_CONTENT,
        provider: IHubContentProvider.MARKETING_NARRATIVES,
    },
    [InsightType.PITCHED_CONTENT]: {
        contentType: IHubContentType.MARKETING_CONTENT,
        provider: IHubContentProvider.MARKETING_NARRATIVES,
    },
    [InsightType.CDB_TEMPLATE]: {
        contentType: IHubContentType.INSIGHTS,
        provider: IHubContentProvider.TEMPLATES,
    },
};
